//frontend/src/components/Chat/UserMessages.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Form, Button, Card, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { FaPaperPlane, FaHeadset, FaRobot, FaPaperclip, FaTimes, FaDownload } from 'react-icons/fa';
import api from '../../services/api';
import { toast } from 'react-toastify';

const UserMessages = () => {
    const { user } = useAuth();
    const [conversation, setConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [startingChat, setStartingChat] = useState(false);
    const chatMessagesRef = useRef(null);
    const fileInputRef = useRef(null);

    const loadMessages = useCallback(async (conversationId) => {
        if (!conversationId) return;
        try {
            const response = await api.get(`/conversations/${conversationId}`);
            setMessages(Array.isArray(response.data.conversation.messages) ? response.data.conversation.messages : []);
            await api.post(`/conversations/${conversationId}/mark-as-read`);
        } catch (err) {
            console.error('Error loading messages:', err);
        }
    }, []);

    const loadOrCreateConversation = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get('/conversations/');
            const conversations = response.data.conversations || [];

            if (conversations.length > 0) {
                const latestConv = conversations.sort((a, b) =>
                    new Date(b.updated_at) - new Date(a.updated_at)
                )[0];
                setConversation(latestConv);
                await loadMessages(latestConv.id);
            }
        } catch (err) {
            console.error('Error loading conversation:', err);
        } finally {
            setLoading(false);
        }
    }, [loadMessages]);

    useEffect(() => {
        loadOrCreateConversation();
    }, [loadOrCreateConversation]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (conversation && conversation.id && !sending) {
                loadMessages(conversation.id);
            }
        }, 10000);

        return () => clearInterval(interval);
    }, [conversation, sending, loadMessages]);

    useEffect(() => {
        if (chatMessagesRef.current) {
            chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
        }
    }, [messages]);

    const handleStartConversation = async () => {
        setStartingChat(true);
        try {
            const response = await api.post('/conversations/');
            setConversation(response.data.conversation);
            await loadMessages(response.data.conversation.id);
            toast.success('Conversation avec le support démarrée');
        } catch (err) {
            console.error('Error starting conversation:', err);
            toast.error('Erreur lors du démarrage de la conversation');
        } finally {
            setStartingChat(false);
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                toast.error('Le fichier ne doit pas dépasser 10MB');
                return;
            }
            setSelectedFile(file);
        }
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if ((!newMessage.trim() && !selectedFile) || !conversation || !conversation.id) return;

        setSending(true);
        try {
            const formData = new FormData();
            formData.append('content', newMessage);
            if (selectedFile) {
                formData.append('file', selectedFile);
            }

            await api.post(`/conversations/${conversation.id}/messages`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            setNewMessage('');
            setSelectedFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            await loadMessages(conversation.id);
        } catch (err) {
            toast.error("Erreur lors de l'envoi du message");
            console.error('Error sending message:', err);
        } finally {
            setSending(false);
        }
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        const today = new Date();
        
        if (date.toDateString() === today.toDateString()) {
            return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        }
        
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        
        if (date.toDateString() === yesterday.toDateString()) {
            return 'Hier ' + date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        }
        
        return date.toLocaleDateString('fr-FR', { 
            day: 'numeric', 
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    };

    const getFileIcon = (fileName) => {
        const ext = fileName?.split('.').pop()?.toLowerCase();
        const icons = {
            pdf: '📄', doc: '📝', docx: '📝', xls: '📊', xlsx: '📊',
            jpg: '🖼️', jpeg: '🖼️', png: '🖼️', gif: '🖼️', zip: '📦', rar: '📦'
        };
        return icons[ext] || '📎';
    };

    if (loading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Chargement...</p>
            </Container>
        );
    }

    return (
        <Container fluid className="py-4" style={{ maxWidth: '1200px' }}>
            <Card className="shadow-lg border-0" style={{ height: 'calc(100vh - 150px)' }}>
                <Card.Header className="bg-primary text-white py-3">
                    <div className="d-flex align-items-center">
                        <div className="me-3">
                            <div className="rounded-circle bg-white d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                <FaHeadset size={25} className="text-primary" />
                            </div>
                        </div>
                        <div>
                            <h5 className="mb-0">Support E-Visa Canada</h5>
                            <small className="opacity-75">
                                <span className="badge bg-success me-2">
                                    <span className="spinner-grow spinner-grow-sm me-1" style={{ width: '8px', height: '8px' }}></span>
                                    En ligne
                                </span>
                                Réponse sous 24h
                            </small>
                        </div>
                    </div>
                </Card.Header>

                <Card.Body className="bg-light p-0" style={{ height: 'calc(100% - 220px)', overflowY: 'auto' }} ref={chatMessagesRef}>
                    {!conversation ? (
                        <div className="d-flex flex-column align-items-center justify-content-center h-100 p-5">
                            <div className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center mb-4" style={{ width: '120px', height: '120px' }}>
                                <FaRobot size={60} className="text-primary" />
                            </div>
                            <h3 className="mb-3">Besoin d aide ?</h3>
                            <p className="text-muted text-center mb-4" style={{ maxWidth: '500px' }}>
                                Notre équipe de support est là pour vous aider avec vos questions concernant votre demande de visa.
                            </p>
                            <Button variant="primary" size="lg" onClick={handleStartConversation} disabled={startingChat} className="px-5">
                                {startingChat ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Démarrage...
                                    </>
                                ) : (
                                    <>
                                        <FaHeadset className="me-2" />
                                        Démarrer une conversation
                                    </>
                                )}
                            </Button>
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="d-flex flex-column align-items-center justify-content-center h-100 p-5">
                            <Alert variant="info" className="text-center">
                                <FaHeadset size={40} className="mb-3" />
                                <h5>Conversation démarrée !</h5>
                                <p className="mb-0">Envoyez votre premier message ci-dessous</p>
                            </Alert>
                        </div>
                    ) : (
                        <div className="p-4">
                            {messages.map((msg, index) => {
                                const isOwnMessage = msg.sender?.id === user.id;
                                const showAvatar = index === 0 || messages[index - 1].sender?.id !== msg.sender?.id;
                                
                                return (
                                    <div key={msg.id} className={`d-flex mb-3 ${isOwnMessage ? 'justify-content-end' : 'justify-content-start'}`}>
                                        {!isOwnMessage && showAvatar && (
                                            <div className="me-2">
                                                <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center text-white" style={{ width: '35px', height: '35px' }}>
                                                    <FaHeadset />
                                                </div>
                                            </div>
                                        )}
                                        
                                        <div style={{ maxWidth: '70%' }}>
                                            <div
                                                className="message-bubble"
                                                style={{
                                                    backgroundColor: isOwnMessage ? '#007bff' : '#ffffff',
                                                    color: isOwnMessage ? '#ffffff' : '#333333',
                                                    border: isOwnMessage ? 'none' : '1px solid #e9ecef',
                                                    borderRadius: isOwnMessage ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                                                    padding: '12px 16px',
                                                    boxShadow: isOwnMessage ? '0 2px 8px rgba(0, 123, 255, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                                                    marginBottom: '4px',
                                                    wordWrap: 'break-word'
                                                }}
                                            >
                                                {msg.content && <p className="mb-0" style={{ fontSize: '14px', lineHeight: '1.4' }}>{msg.content}</p>}

                                                {msg.file && (
                                                    <div
                                                        className="file-attachment mt-2 p-2 rounded"
                                                        style={{
                                                            backgroundColor: isOwnMessage ? 'rgba(255, 255, 255, 0.2)' : '#f8f9fa',
                                                            border: isOwnMessage ? 'none' : '1px solid #dee2e6'
                                                        }}
                                                    >
                                                        <div className="d-flex align-items-center">
                                                            <span className="me-2 fs-4">{getFileIcon(msg.file_name)}</span>
                                                            <div className="flex-grow-1">
                                                                <small className="d-block fw-bold" style={{ color: isOwnMessage ? '#ffffff' : '#333333' }}>{msg.file_name}</small>
                                                                <small style={{ color: isOwnMessage ? 'rgba(255, 255, 255, 0.7)' : '#6c757d' }}>
                                                                    {msg.file_size ? `${msg.file_size} MB` : 'Fichier'}
                                                                </small>
                                                            </div>
                                                            <a
                                                                href={`${api.defaults.baseURL.replace('/api', '')}${msg.file}`}
                                                                download
                                                                className={`btn btn-sm ${isOwnMessage ? 'btn-light' : 'btn-outline-primary'}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ fontSize: '12px', padding: '4px 8px' }}
                                                            >
                                                                <FaDownload />
                                                            </a>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <small
                                                className={`d-block mt-1 ${isOwnMessage ? 'text-end' : 'text-start'} text-muted`}
                                                style={{ fontSize: '12px' }}
                                            >
                                                {formatTime(msg.created_at)}
                                                {isOwnMessage && msg.is_read && <span className="ms-2" style={{ color: '#28a745' }}>✓✓</span>}
                                            </small>
                                        </div>
                                        
                                        {isOwnMessage && showAvatar && (
                                            <div className="ms-2">
                                                <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white" style={{ width: '35px', height: '35px', fontSize: '14px' }}>
                                                    {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Card.Body>

                {conversation && (
                    <Card.Footer className="bg-white border-top-0 p-3">
                        {selectedFile && (
                            <Alert variant="info" className="mb-2 py-2">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center">
                                        <span className="me-2 fs-4">{getFileIcon(selectedFile.name)}</span>
                                        <div>
                                            <small className="fw-bold d-block">{selectedFile.name}</small>
                                            <small className="text-muted">{formatFileSize(selectedFile.size)}</small>
                                        </div>
                                    </div>
                                    <Button variant="link" size="sm" className="text-danger" onClick={handleRemoveFile}>
                                        <FaTimes />
                                    </Button>
                                </div>
                            </Alert>
                        )}
                        
                        <Form onSubmit={handleSendMessage}>
                            <div className="d-flex gap-2">
                                <input type="file" ref={fileInputRef} onChange={handleFileSelect} style={{ display: 'none' }} accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.zip,.rar" />
                                
                                <Button variant="outline-secondary" onClick={() => fileInputRef.current?.click()} className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }} title="Joindre un fichier">
                                    <FaPaperclip size={20} />
                                </Button>
                                
                                <Form.Control type="text" value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder="Écrivez votre message..." disabled={sending} className="border-2" style={{ borderRadius: '25px', padding: '12px 20px' }} />
                                
                                <Button type="submit" variant="primary" disabled={(!newMessage.trim() && !selectedFile) || sending} className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                    {sending ? <Spinner animation="border" size="sm" /> : <FaPaperPlane />}
                                </Button>
                            </div>
                        </Form>
                        
                        <div className="text-center mt-2">
                            <small className="text-muted">📎 Fichiers acceptés : PDF, Word, Excel, Images (Max 10MB)</small>
                        </div>
                    </Card.Footer>
                )}
            </Card>

            <div className="mt-3 text-center">
                <small className="text-muted">🔒 Vos conversations et fichiers sont sécurisés et confidentiels</small>
            </div>
        </Container>
    );
};

export default UserMessages;